/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    //AddToCartComponent,
    //AddToOrderTemplateComponent,
    IPriceComponentResources,
    ISwatchItem,
    //PriceComponent,
    ProductComponentSwatchComponent,
    RatingComponent,
    IOrderTemplateNameDialogResources,
    IAddToOrderTemplateDialogResources,
    IItemAddedToOrderTemplateDialogResources,
    IDuplicateItemsWhenAddingToOrderTemplateDialogResources
} from '@msdyn365-commerce/components';
import { PriceComponent } from './price.component';
import {
    getAsset,
    getUrlSync,
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    ImageFetchPriority,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import {
    AttributeSwatch,
    AttributeValue,
    Customer, ProductList,
    ProductPrice,
    ProductSearchResult,
    SimpleProduct,
    UnitOfMeasureConversion
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    IDimensionsApp,
    StringExtensions,
    OrderTemplate
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent, Node,
    onTelemetryClick,
    UncontrolledTooltip
} from '@msdyn365-commerce-modules/utilities';
import { ICartState } from '@msdyn365-commerce/global-state';
import { AddToCartComponent, IAddToCartToastResources } from '../../../../modules/dlvry-buybox/common/add-to-cart.component';
import { toast } from '../../../../components/toast/toast';
import AddToOrderTemplateComponent from './addtoordertemplate.component';
import PQueue from 'p-queue';
import { IProductInventoryInformation } from '../../../../components/common/product-inventory-utils';
import { humanizeUnitOfMeasure, UnitOfMeasure } from '../../../../components/unit-of-measure';
import DiscountBadge from '../../../../components/discount-badge/discount-badge';
import QuantityInput from '../../../../components/common/quantity-input';
import { getTemperatureZoneIcon } from '../../../../shared-utils/attributes/get-temperature-zone';
import { multiplyPriceBy } from '../../../../shared-utils/price-utils';
import { IProductAgreement, ProductSearchResultExt, SimpleProductExt } from '../../../../shared-utils/types';
import { getAgreementIconSource } from '../../../../shared-utils/agreement-icon-utils';
import { IProductListByProductId } from '../../../../actions/get-product-lists-by-product-ids';
import { getConversionText, getKilogramConversion, getLiterPrice, getNonBreakoffUOM, getPcsConversion, getRoundedFactor } from '../../../../shared-utils/unit-conversion-utils';
import UnitConversionDetails from '../../../../components/UnitConversionDetails';
import { cssCapitalize, formatCurrencyNOK, getProductPageUrlWithCategoryPathSync, getShowAlternativeProductsType, isAvailableToBuy, isProductInCategory } from '../../../../shared-utils/common-utils';
import { IBrandSchemeEntity } from '../../../../actions/entities/brand-scheme-entity';
import { ensureBaseImageUrl } from '../../../../shared-utils/image-utilities';
import { IAppSettingsInContext } from '../../../../settings/app.settings.autogenerated';
import CartCount from '../../../../components/cart-count';
import { getHideConversionInCategories } from '../../../../settings/app.settings.utils';
import { LifecycleStatus } from '../../../../shared-utils/lifecycle-status';
import { LocalDropdown } from '../../../../components/dropdown/dropdown';
import { AlternativeProducts, OnChangeProductFunction } from '../../../../components/dropdown/alternative-products';
import { IProductRelationTypeForProduct } from '../../../../actions/get-relation-types-by-ids';
import * as GTM from '../../../../shared-utils/google-tag-manager/gtm';
import { ExpandableEllipsis } from '../../../../components/common/expandable-ellipsis';
import { getAgreementBadgeForProduct } from '../../../../shared-utils/agreement-product-utils';
import { AgreementMark } from '../../../../components/icons';

export interface IProductComponentProps extends IComponentProps<{
    cart?: ICartState,
    product?: ProductSearchResultExt,
    simpleProduct?: SimpleProduct,
    orderTemplates?: OrderTemplate[],
    customerInformation?: Customer,
    customerAgreements?: IProductAgreement[],
    unitConversions?: UnitOfMeasureConversion[],
    brandSchemes?: IBrandSchemeEntity[];
    relationTypesByIds?: IProductRelationTypeForProduct[];
}> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    depositSuffixText?: string;
    depositLabelText?: string;
    pricePerText?: string;
    priceNotLoggedInText?: string;
    cartQueue?: PQueue;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    wishlistButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    disableBuyButton?: boolean;
    showPrice?: boolean;
    showAsPlaceholder?: boolean;
    userHasAccess?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    inventoryStatus?: IProductInventoryInformation;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
    addToCartText?: string;
    selectQuantityText?: string;
    updateCartText?: string,
    removeFromCartText?: string;
    expiredText?: string;
    outOfSeasonBuyButtonText?: string;
    outOfStockText?: string;
    priceErrorText?: string;
    toastStrings?: IAddToCartToastResources;
    inCartTooltipText?: string;
    transitTooltip?: string;
    agreementProductTooltip?: string;
    alternativeProductsButtonText?: string;
    alternativeProductsChangeProductButtonText?: string;
    alternativeProductsLoadingText?: string;
    alternativeProductsNoResultsText?: string;
    alternativeProductsErrorText?: string;
    alternativeProductsCloseButtonText?: string;
    addToOrderTemplateResources?: IAddToOrderTemplateResources;
    currentQuantity?: number;
    breakoff?: boolean;
    alternativeProductsMode?: 'edit' | 'view';
    onUpdateQuantity?: (product: ProductSearchResultExt, quantity: number) => void;
    onBreakoffChange?: (product: ProductSearchResultExt, breakoff: boolean) => void;
    onChangeProduct?: OnChangeProductFunction;
    onUpdateOrderTemplates?: (product?: ProductSearchResultExt, orderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    retrieveOrderTemplates?(): Promise<OrderTemplate[]>;
    onBuyValidate?: (product: ProductSearchResultExt, quantity: number) => Promise<BuyValidationResult>;
    compactView?: boolean;
    productIndex?: number;
}

export interface IProductComponent extends IComponent<IProductComponentProps> {
}

export interface IAddToOrderTemplateResources {
    addToOrderTemplateHeader: string;
    noOrderTemplatesMessage: string;
    noOrderTemplatesDescription: string;
    createAnOrderTemplateButtonText: string;
    createNewOrderTemplateButtonText: string;
    cancelOrderTemplateCreationButtonText: string;
    selectTemplatesText: string;
    addToTemplateButtonText: string;
    lineItemsText: string;
    createOrderTemplateHeader: string;
    orderTemplateTitle: string;
    orderTemplateNameAriaLabel: string;
    createOrderTemplateDescription: string;
    defaultOrderTemplateName: string;
    createOrderTemplateButtonText: string;
    cancelNewOrderTemplateCreationButtonText: string;
    viewOrderTemplateButtonText: string;
    continueShoppingButtonText: string;
    itemAddedToOrderTemplateHeaderItemOneText: string;
    itemAddedToOrderTemplateHeaderItemFormatText: string;
    itemAddedToOrderTemplateHeaderMessageText: string;
    priceFree: string;
    originalPriceText: string;
    currentPriceText: string;
    duplicatedProductsHeader: string;
    duplicatedProductsDescription: string;
    updateQuantityButtonText: string;
    cancelDuplicateItemsButtonText: string;
    addToOrderTemplateButtonText: string;
    addToOrderTemplateButtonTooltip: string;
}

export interface BuyValidationResult {
    isValid: boolean;
    message?: string;
}

const PriceComponentActions = {};
const highPriorityProductComponentCount = 9;

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the react node for product unit of measure display.
 * @param  unitOfMeasure - DefaultUnitOfMeasure property from product.
 * @returns The node representing markup for unit of measure component.
 */
// @ts-expect-error
function renderProductUnitOfMeasure(context: ICoreContext, unitOfMeasure?: string): JSX.Element | null {
    if (!unitOfMeasure) {
        return null;
    }
    return (
        <UnitOfMeasure context={context} unitOfMeasure={unitOfMeasure} />
    );
}

/**
 * Gets the react node for product availability.
 * @param inventoryAvailabilityLabel - The product information.
 * @returns The node representing markup for product availability.
 */
function renderProductAvailability(
    context: ICoreContext,
    inventoryAvailabilityLabel: string | undefined,
    inventoryStatus?: IProductInventoryInformation,
    simpleProduct?: SimpleProduct,
    compact = false
): JSX.Element | null {
    const emptyText = <>&nbsp;</>;
    const renderAvailability = (className: string, text: React.ReactNode = emptyText) => {
        if (compact) {
            return (
                <div className='msc-product__compact-availability-wrapper'>
                    <div className={classnames('msc-product__compact-availability', className)} title={typeof text === 'string' ? text : undefined}>
                        <div className='msc-product__compact-availability-indicator' />
                        <div className='msc-product__compact-availability-text'>{text}</div>
                    </div>
                </div>
            );
        }

        return (
            <ExpandableEllipsis
                className='ellipsisCompact'
                overlayClassName='msc-product-component__availability-ellipsis-overlay'
                minWidth={200}
            >
                <div className={classnames('msc-product__availability', className)}>
                    <div className='msc-product__availability__indicator' />
                    <div className='text-overflow-target'>{text}</div>
                </div>
            </ExpandableEllipsis>
        );
    };

    if (!simpleProduct) {
        return renderAvailability('retrieving');
    }

    const lifeCycleStatus = simpleProduct?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
    if (lifeCycleStatus === LifecycleStatus.OutOfSeason) {
        return renderAvailability('hidden');
    }

    const normalizeLabel = (label?: string) => label?.startsWith('!') ? label.substring(1) : label;

    if (inventoryStatus?.StockLevelCode === 'ERR') {
        const stockLevelLabel = normalizeLabel(inventoryStatus?.StockLevelLabel);
        return renderAvailability('error', stockLevelLabel  || 'ERROR');
    }

    const availabilityLabel = normalizeLabel(inventoryAvailabilityLabel);

    if (inventoryStatus?.IsProductAvailable || inventoryStatus?.StockLevelLabel?.startsWith('!')) {
        return renderAvailability('available', availabilityLabel || emptyText);
    }
    
    if (inventoryStatus?.IsProductAvailable === false) {
        return renderAvailability('not-available', availabilityLabel || emptyText);
    }

    return renderAvailability('retrieving');
}

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

function generateAddToOrderTemplateDialogResources(resources: IAddToOrderTemplateResources | undefined): IAddToOrderTemplateDialogResources {
    return {
        addToOrderTemplateHeader: resources?.addToOrderTemplateHeader || 'addToOrderTemplateHeader',

        noOrderTemplatesMessage: resources?.noOrderTemplatesMessage || 'noOrderTemplatesMessage',
        noOrderTemplatesDescription: resources?.noOrderTemplatesDescription || 'noOrderTemplatesDescription',

        createAnOrderTemplateButtonText: resources?.createAnOrderTemplateButtonText || 'createAnOrderTemplateButtonText',
        createNewOrderTemplateButtonText: resources?.createNewOrderTemplateButtonText || 'createNewOrderTemplateButtonText',
        cancelOrderTemplateCreationButtonText: resources?.cancelOrderTemplateCreationButtonText || 'cancelOrderTemplateCreationButtonText',

        selectTemplatesText: resources?.selectTemplatesText || 'selectTemplatesText',
        addToTemplateButtonText: resources?.addToTemplateButtonText || 'addToTemplateButtonText',
        lineItemsText: resources?.lineItemsText || 'lineItemsText',
    };
}

function generateOrderTemplateNameDialogResources(resources: IAddToOrderTemplateResources | undefined): IOrderTemplateNameDialogResources {
    return {
        orderTemplateHeaderLabel: resources?.createOrderTemplateHeader || 'createOrderTemplateHeader',
        orderTemplateTitleLabel: resources?.orderTemplateTitle || 'orderTemplateTitle',
        orderTemplateNameAriaLabel: resources?.orderTemplateNameAriaLabel || 'orderTemplateNameAriaLabel',
        orderTemplateDescription: resources?.createOrderTemplateDescription || 'createOrderTemplateDescription',

        defaultOrderTemplateName: resources?.defaultOrderTemplateName || 'defaultOrderTemplateName',

        orderTemplateButtonLabel: resources?.createOrderTemplateButtonText || 'createOrderTemplateButtonText',
        orderTemplateCancelButtonLabel: resources?.cancelNewOrderTemplateCreationButtonText || 'cancelNewOrderTemplateCreationButtonText',
    };
}

function generateItemAddedToOrderTemplateDialogResources(resources: IAddToOrderTemplateResources | undefined): IItemAddedToOrderTemplateDialogResources {
    return {
        viewOrderTemplateButtonText: resources?.viewOrderTemplateButtonText || 'viewOrderTemplateButtonText',
        continueShoppingButtonText: resources?.continueShoppingButtonText || 'continueShoppingButtonText',
        itemAddedToOrderTemplateHeaderItemOneText: resources?.itemAddedToOrderTemplateHeaderItemOneText || 'itemAddedToOrderTemplateHeaderItemOneText',
        itemAddedToOrderTemplateHeaderItemFormatText: resources?.itemAddedToOrderTemplateHeaderItemFormatText || 'itemAddedToOrderTemplateHeaderItemFormatText',
        itemAddedToOrderTemplateHeaderMessageText: resources?.itemAddedToOrderTemplateHeaderMessageText || 'itemAddedToOrderTemplateHeaderMessageText',
        freePriceText: resources?.priceFree || 'priceFree',
        originalPriceText: resources?.originalPriceText || 'originalPriceText',
        currentPriceText: resources?.currentPriceText || 'currentPriceText',
    };
}

function generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(resources: IAddToOrderTemplateResources | undefined): IDuplicateItemsWhenAddingToOrderTemplateDialogResources {
    return {
        duplicatedProductsHeader: resources?.duplicatedProductsHeader || 'duplicatedProductsHeader',
        duplicatedProductsDescription: resources?.duplicatedProductsDescription || 'duplicatedProductsDescription',
        updateQuantityButtonText: resources?.updateQuantityButtonText || 'updateQuantityButtonText',
        cancelDuplicateItemsButtonText: resources?.cancelDuplicateItemsButtonText || 'cancelDuplicateItemsButtonText',
    };
}

const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    ratingAriaLabel,
    ratingCountAriaLabel,
    depositSuffixText,
    depositLabelText,
    pricePerText,
    priceNotLoggedInText,
    productIndex,
    cartQueue,
    allowBack,
    typeName,
    id,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    wishlistButton,
    isEnabledProductDescription,
    isPriceMinMaxEnabled,
    disableBuyButton,
    showPrice = true,
    priceResources,
    inventoryLabel,
    inventoryStatus,
    dimensionAvailabilities,
    swatchItemAriaLabel,
    addToCartText,
    selectQuantityText,
    updateCartText,
    removeFromCartText,
    outOfStockText,
    priceErrorText,
    toastStrings,
    inCartTooltipText,
    transitTooltip,
    agreementProductTooltip,
    addToOrderTemplateResources,
    currentQuantity,
    onUpdateQuantity,
    onUpdateOrderTemplates,
    retrieveOrderTemplates,
    onBuyValidate,
    compactView = false,
    ...props
}) => {
    const { product, simpleProduct, customerAgreements } = data;
    const compactDetailsPanelId = `msc-product-compact-details-${id}-${productIndex || 0}`.replace(/[^a-zA-Z0-9_-]/g, '');
    const compactDetailsPanelHeadingId = `${compactDetailsPanelId}-heading`;
    const [orderQuantity, setOrderQuantity] = useState(currentQuantity ?? 0);
    const [internalBreakoff, setInternalBreakoff] = useState(props.breakoff ?? false);

    // Random width between 90px and 150px to make the loading state look more natural
    const randomWidth = useMemo(() => Math.floor(Math.random() * (150 - 90 + 1)) + 90, []);

    // Use internal state if no onBreakoffChange is provided
    const breakoff = props.onBreakoffChange !== undefined ? props.breakoff : internalBreakoff;
    const onBreakoffChange = props.onBreakoffChange !== undefined ? props.onBreakoffChange : (_: any, newBreakoff: boolean) => setInternalBreakoff(newBreakoff);

    const orderTemplateIndicatorRef = React.createRef<HTMLDivElement>();

    // TEST: Capitalize product name once
    if (product && product.Name && !(product as any).__nameCapitalized) {
        product.Name = cssCapitalize(product.Name, context.request.locale);
        (product as any).__nameCapitalized = true;
    }

    let productUrl = getProductPageUrlWithCategoryPathSync(context.actionContext, product, product?.CategoryPaths) || '';
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    const [cardActionsOpen, setCardActionsOpen] = useState<boolean>(false);
    const [showUnitConversionDetails, setShowUnitConversionDetails] = useState<boolean>(false);
    const cardActionWrapperRef = React.createRef<HTMLDivElement>();
    const temperatureZoneIcon = React.useMemo(() => product?.TemperatureZone ? getTemperatureZoneIcon(context, product.TemperatureZone) : undefined, [product]);

    // @ts-expect-error -- Variable is declared but its value is never read.
    const [forceUpdate, setForceUpdate] = useState<number>(0);

    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);

    React.useEffect(() => {
        // Set 'title' attribute on AddToOrderTemplate buttons
        document.querySelectorAll('.order-template-button').forEach(element => {
            element.setAttribute('title', element.getAttribute('aria-label') || '');
        });
    }, []);
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = context.app.config.enableStockCheck;

    const siteContext = context as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
    const multiplier = breakoff ? 1 : simpleProduct?.Behavior?.MultipleOfQuantity || 1;
    const quantityIsNotDefault = orderQuantity !== 0 && orderQuantity !== multiplier;

    /**
     * Updates the product page and Image url based on swatch selected.
     * @param coreContext - Context of the caller.
     * @param swatchItem - Dimension swatch selected.
     */
    const updatePageAndImageUrl = React.useCallback(
        (coreContext: ICoreContext, swatchItem: ISwatchItem) => {
            const dimensionType = swatchItem.dimensionType;
            selectedSwatchItems.setValue(dimensionType, swatchItem);
            if (StringExtensions.isNullOrWhitespace(swatchItem.value)) {
                return;
            }
            const queryString = `${dimensionType}=${swatchItem.value}`;
            let productPageUrlWithSwatch = '';
            if (productPageUrl.includes(dimensionType)) {
                const newUrl = new URL(productPageUrl, coreContext.request.apiSettings.baseUrl);
                newUrl.searchParams.delete(dimensionType);
                productPageUrlWithSwatch = updateProductUrl(newUrl.toString(), context, queryString);
            } else {
                productPageUrlWithSwatch = updateProductUrl(productPageUrl, context, queryString);
            }
            setProductPageUrl(productPageUrlWithSwatch);
            if (dimensionType === dimensionToPreSelectInProductCard) {
                const swatchProductImageUrl = ArrayExtensions.hasElements(swatchItem.productImageUrls)
                    ? swatchItem.productImageUrls[0]
                    : undefined;
                const newImageUrl = generateImageUrl(swatchProductImageUrl, coreContext.request.apiSettings);
                setProductImageUrl(newImageUrl);
            }
        },
        [selectedSwatchItems, context, productPageUrl]
    );

    if (!product) {
        return null;
    }

    /**
     * Checks if rendering the particular dimensions is allowed for product card.
     * @param dimensionType - Dimension to be displayed.
     * @returns Updates the state with new product page url.
     */
    function shouldDisplayDimension(dimensionType: string): boolean {
        const dimensionsContext = context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
        return (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
        );
    }

    /**
     * Gets the react node for product dimension as swatch.
     * @param  attributeValues - Attribute value property from product.
     * @returns The node representing markup for unit of measure component.
     */
    function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
        if (!attributeValues) {
            return null;
        }

        return (
            <div className='msc-product__dimensions'>
                {attributeValues.map((item: AttributeValue) => {
                    const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                    if (!shouldDisplayDimension(dimensionTypeValue)) {
                        return null;
                    }

                    const siteContext = context as ICoreContext<IDimensionsApp>;
                    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
                    const dimensionType = dimensionTypeValue as DimensionTypes;
                    const swatches =
                        item.Swatches?.map<ISwatchItem>(swatchItem => {
                            return {
                                itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
                                value: swatchItem.SwatchValue ?? '',
                                dimensionType,
                                colorHexCode: swatchItem.SwatchColorHexCode,
                                imageUrl: swatchItem.SwatchImageUrl,
                                productImageUrls: swatchItem.ProductImageUrls,
                                isDefault: swatchItem.IsDefault,
                                swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
                                isDisabled:
                                    enableStockCheck &&
                                    dimensionAvailabilities?.find(
                                        dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
                                    )?.isDisabled
                            };
                        }) ?? [];
                    if (
                        dimensionType === dimensionToPreSelectInProductCard &&
                        ArrayExtensions.hasElements(swatches) &&
                        !swatches.some(swatch => swatch.isDefault)
                    ) {
                        swatches[0].isDefault = true;
                    }
                    return (
                        <ProductComponentSwatchComponent
                            context={context}
                            swatches={swatches}
                            onSelectDimension={updatePageAndImageUrl}
                            key={item.RecordId}
                        />
                    );
                })}
            </div>
        );
    }

    function renderOrderTemplateButton() {
        if (!data.customerInformation?.IsB2b) {
            return null;
        }

        return (
            <div>
                <AddToOrderTemplateComponent
                    className='order-template-button'
                    addToOrderTemplateButtonText={addToOrderTemplateResources?.addToOrderTemplateButtonText || 'addToOrderTemplateButtonText'}
                    addToOrderTemplateButtonTooltip={addToOrderTemplateResources?.addToOrderTemplateButtonTooltip || 'addToOrderTemplateButtonTooltip'}
                    addToOrderTemplateDialogResources={generateAddToOrderTemplateDialogResources(addToOrderTemplateResources)}
                    createOrderTemplateDialogResources={generateOrderTemplateNameDialogResources(addToOrderTemplateResources)}
                    itemAddedToOrderTemplateDialogResources={generateItemAddedToOrderTemplateDialogResources(addToOrderTemplateResources)}
                    duplicateItemsWhenAddingToOrderTemplateDialogResources={generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(addToOrderTemplateResources)}
                    context={context}
                    id={id}
                    typeName={typeName}
                    data={{
                        product: {
                            RecordId: data.product?.RecordId!,
                            ItemId: data.product?.ItemId,
                            Name: data.product?.Name,
                            Description: data.product?.Description,
                            ProductTypeValue: data.product?.RecordId!,
                            DefaultUnitOfMeasure: data.product?.DefaultUnitOfMeasure,
                            BasePrice: data.product?.BasePrice!,
                            Price: data.product?.Price!,
                            AdjustedPrice: data.product?.Price!,
                            MasterProductId: data.product?.MasterProductId,
                            PrimaryImageUrl: data.product?.PrimaryImageUrl,
                            ExtensionProperties: data.product?.ExtensionProperties
                        },
                        quantity: orderQuantity || 1,
                        customerInformation: data.customerInformation,
                        partOfProductLists: data.product?.PartOfProductLists
                    }}
                    onSuccess={async (orderTemplate) => {
                        await onUpdateOrderTemplates?.(data.product, orderTemplate);
                        toast({
                            text: `${data.product?.Name}${addToOrderTemplateResources?.itemAddedToOrderTemplateHeaderMessageText}`,
                            autoClose: 3000,
                            canClose: true,
                            position: 'bottom-right',
                            type: 'success',
                            showProgress: true
                        });
                    }}
                    retrieveOrderTemplates={retrieveOrderTemplates}
                />
            </div>
        );
    }

    function renderWishlistButton(wishlistButton: React.ReactNode): React.ReactNode | undefined {
        return wishlistButton;
    }

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    // @ts-expect-error
    const isUnitOfMeasureEnabled = context.app.config && context.app.config.unitOfMeasureDisplayType === 'buyboxAndBrowse';
    const _renderCartButton = (): JSX.Element | null => {
        if (!context.request.user.isAuthenticated) {
            return null;
        }

        const product1: SimpleProduct = {
            RecordId: data.product?.RecordId!,
            ItemId: data.product?.ItemId,
            Name: data.product?.Name,
            Description: data.product?.Description,
            ProductTypeValue: data.product?.RecordId!,
            DefaultUnitOfMeasure: data.product?.DefaultUnitOfMeasure,
            BasePrice: data.product?.BasePrice!,
            Price: data.product?.Price!,
            AdjustedPrice: data.product?.Price!,
            MasterProductId: data.product?.MasterProductId,
            PrimaryImageUrl: data.product?.PrimaryImageUrl,
            ExtensionProperties: data.product?.ExtensionProperties
        };
        const productDimensionAvailabilities =
            dimensionAvailabilities &&
            dimensionAvailabilities?.find(
                dimensionAvailability =>
                    ArrayExtensions.hasElements(dimensionAvailabilities) &&
                    dimensionAvailability.masterProductId &&
                    dimensionAvailability.masterProductId === product.MasterProductId
            );

        return (
            <Node className='ms-search-result-container__add-to-cart-container'>
                <AddToCartComponent
                    addToCartText={addToCartText ? addToCartText : 'Legg i kurv'}
                    updateCartText={updateCartText ? updateCartText : 'Oppdater'}
                    removeFromCartText={removeFromCartText ? removeFromCartText : 'Fjern fra kurv'}
                    outOfStockText={outOfStockText ? outOfStockText : 'Ikke på lager'}
                    expiredText={props.expiredText}
                    outOfSeasonBuyButtonText={props.outOfSeasonBuyButtonText}
                    hasAvailableProducts={productDimensionAvailabilities?.isDisabled}
                    productAvailability={inventoryStatus?.ProductAvailableQuantity || { AvailableQuantity: 0 }}
                    data={{ cart: data.cart, product: data.simpleProduct || product1, productSearch: data.product }}
                    cartQueue={cartQueue}
                    context={context}
                    id={id}
                    disabled={disableBuyButton}
                    typeName={typeName}
                    quantity={orderQuantity}
                    breakoff={breakoff}
                    nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, data.unitConversions)}
                    toastStrings={toastStrings}
                    onUpdateQuantity={(quantity: number) => {
                        setOrderQuantity(quantity);
                        onUpdateQuantity?.(product, quantity);
                    }}
                    onBuyValidate={onBuyValidate}
                    onAdd={e => {
                        setForceUpdate((prev) => prev + 1);
                    }}
                />
            </Node>
        );
    };

    const renderCompactDeposit = (): JSX.Element | null => {
        const deposit = context.app.config?.enableDepositPriceDisplay && simpleProduct?.ExtensionProperties?.find(x => x.Key === 'DEPOSIT')?.Value?.DecimalValue;
        const quantityMultiplier = breakoff
            ? orderQuantity
            : orderQuantity / (simpleProduct?.Behavior?.MultipleOfQuantity || 1);
        const totalDeposit = deposit && deposit * Math.max(1, quantityMultiplier);

        if (!compactView || !totalDeposit || totalDeposit <= 0) {
            return null;
        }

        return (
            <div className='msc-product__compact-deposit'>
                <span className='msc-product__compact-deposit-label'>{depositLabelText}</span>
                <span className='msc-product__compact-deposit-value'>
                    <span>+{' '}</span>
                    <PriceComponent
                        context={context as ICoreContext<IAppSettingsInContext>}
                        id={id}
                        className='deposit'
                        typeName={typeName}
                        data={{ price: { CustomerContextualPrice: totalDeposit } }}
                        savingsText={savingsText}
                        freePriceText={freePriceText}
                        originalPriceText={originalPriceText}
                        isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                        priceResources={priceResources}
                    />
                </span>
            </div>
        );
    };

    const renderPurchaseControls = (additionalDetails?: React.ReactNode): JSX.Element => (
        <>
            {context.request.user.isAuthenticated && (
                <div className='msc-product__quantity-wrapper' style={{ visibility: !allowBuying ? 'hidden' : undefined }}>
                    <QuantityInput
                        context={context}
                        disable={!allowBuying}
                        product={simpleProduct}
                        extProduct={product}
                        quantity={orderQuantity}
                        breakoff={!!breakoff}
                        onQuantityChange={(newQuantity) => {
                            setOrderQuantity(newQuantity);
                            onUpdateQuantity?.(product, newQuantity);
                        }}
                        onBreakoffChange={(newBreakoff) => onBreakoffChange?.(product, newBreakoff)}
                        nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, data.unitConversions)}
                        unitConversions={data.unitConversions}
                    />
                </div>
            )}
            {outOfSeasonText && (
                <div className='msc-product__out-of-season-indicator'>
                    <div className='msc-product__out-of-season-indicator__text'>
                        {outOfSeasonText}
                    </div>
                </div>
            )}
            {context.request.user.isAuthenticated && allowBuying && (
                <div className={classnames('msc-product__total-price__container', { changed: quantityIsNotDefault, retrieving: !showPrice || !simpleProduct })}>
                    <div className='msc-product__total-price__label'>
                        Totalt:
                    </div>
                    <PriceComponent
                        context={context}
                        className={classnames('msc-product__total-price__price', { error: product.PriceFailed })}
                        data={{
                            price: {
                                CustomerContextualPrice: (product.ProductPrice?.CustomerContextualPrice || 0) * (orderQuantity || 0),
                            }
                        }}
                        id={id}
                        typeName={typeName}
                        freePriceText=' '
                    />
                </div>
            )}
            {additionalDetails}
        </>
    );

    const _renderOrderTemplateIndicator = (): JSX.Element | undefined => {
        let orderTemplateIndicator: JSX.Element | undefined = undefined;

        if (!props.showAsPlaceholder && product.PartOfProductLists && product.PartOfProductLists.length > 0) {
            orderTemplateIndicator = (
                <div style={{ display: 'inline-block' }}>
                    <div
                        ref={orderTemplateIndicatorRef}
                        className={`${baseClassName}__badge order-template-indicator`}
                    >
                        P
                    </div>
                    <UncontrolledTooltip
                        className={`${baseClassName}__order-template-indicator__tooltip`}
                        placement='bottom-start'
                        trigger='hover focus'
                        delay={{ show: 200, hide: 200 }}
                        target={orderTemplateIndicatorRef}
                    >
                        <div className={`${baseClassName}__order-template-indicator__tooltip-header`}>
                            {/* inOrderTemplateTooltipHeader ||  */'Finnes i plankjøp:'}
                        </div>
                        {product.PartOfProductLists && product.PartOfProductLists.length > 0 && (
                            <ul>
                                {product.PartOfProductLists.map((productList: IProductListByProductId) => {
                                    let url = getUrlSync('ordertemplate', context.actionContext) ?? '';
                                    const separator = url.includes('?') ? '&' : '?';

                                    if (productList.ProductListId) {
                                        url = `${url}${separator}id=${productList.ProductListId}`;
                                    }

                                    return (
                                        <li key={productList.ProductListId}>
                                            <a href={url}>{productList.Name}</a>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </UncontrolledTooltip>
                </div>
            );
        }

        return orderTemplateIndicator;
    };

    const baseClassName = 'msc-product-component';
    const additionalInformation = (product.AlcoholProduct && product.AlcoholProduct > 0 && product.ImporterGrouping) && (
        <div className='msc-product__title__importer-grouping'>{product.ImporterGrouping}</div>
    ) || product.Brand && (
        <div className='msc-product__title__brand'>{product.Brand}</div>
    ) || product.Manufacturer && (
        <div className='msc-product__title__manufacturer'>{product.Manufacturer}</div>
    ) || product.Producer && (
        <div className='msc-product__title__producer'>{product.Producer}</div>
    );
    const unitConversionDetailsEnabled = context.app.config?.enableUnitConversionDetails;
    const lifeCycleStatus = simpleProduct?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
    const isOutOfSeason = lifeCycleStatus === LifecycleStatus.OutOfSeason;
    const relationTypesForProduct = data.relationTypesByIds?.filter(x => x.ProductId === product.RecordId) || [];
    const showAlternativeProductsType = getShowAlternativeProductsType(context, relationTypesForProduct, inventoryStatus);
    const allowBuying = isAvailableToBuy(context, simpleProduct, showAlternativeProductsType, inventoryStatus);
    const agreementBadge = getAgreementBadgeForProduct(product, customerAgreements);
    const outOfSeasonText = !allowBuying && isOutOfSeason
        ? 'Varen er ute av sesong'
        : showAlternativeProductsType === 'Primary' && inventoryStatus?.StockLevelCode === 'OOS'
            ? 'Varen er sluttsolgt'
            : undefined;

    // Get customer agreements that apply for this product
    const productAgreements = customerAgreements?.filter(x => product.Agreements?.find(y => y.AttributeRecordId === x.AttributeRecordId));
    // Get list of all agreement icons that should be shown for this product. Filter out duplicates
    const agreementIcons = productAgreements?.filter(x => x.IconFileName && (x.DisplayType === 'both' || x.DisplayType === 'icon')).reduce((acc, curr) => {
        if (!acc.find(x => x.IconFileName === curr.IconFileName)) {
            acc.push(curr);
        }
        return acc;
    }, [] as IProductAgreement[]);
    // Check if there is any agreements without a specified icon, show a single generic icon if so
    const hasAgreementWithoutIcon = productAgreements?.some(x => !x.IconFileName && (x.DisplayType ? x.DisplayType === 'both' || x.DisplayType === 'icon' : true));

    return (
        <div className={classnames(baseClassName, { shimmer: props.showAsPlaceholder })}>
            {isEnabledProductDescription ? (
                <a
                    href={productPageUrl}
                    onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                    aria-label={renderLabel(
                        product.Name,
                        formatCurrencyNOK(product.Price),
                        product.AverageRating,
                        ratingAriaLabel,
                        product.TotalRatings,
                        ratingCountAriaLabel
                    )}
                    className={classnames('msc-product', { 'logged-in': context.request.user.isAuthenticated })}
                    {...attribute}
                >
                    <div className='msc-product__image__description'>
                        <div className='msc-product__image-container'>
                            <div className='msc-product__image'>
                                {renderProductPlacementImage(
                                    imageSettings,
                                    context.request.gridSettings,
                                    productImageUrl,
                                    product.PrimaryImageUrl,
                                    product.Name,
                                    context,
                                    productIndex !== undefined && productIndex < highPriorityProductComponentCount ? ImageFetchPriority.High : undefined
                                )}
                            </div>
                            {product.SpecialBadges && (
                                <div className='msc-product__special-badges'>
                                    {product.SpecialBadges.split('|').map((badge: string, index: number) => {
                                        const brandScheme = data.brandSchemes?.find(x => x.BrandSchemeId === badge);

                                        return (
                                            <img
                                                key={`${product.RecordId}-${brandScheme?.BrandSchemeId}`}
                                                src={brandScheme?.ImageUrl}
                                                alt={brandScheme?.BrandSchemeName}
                                                title={brandScheme?.BrandSchemeName} />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className='msc-product__title_description'>
                            <h5 className='msc-product__title__text'>{product.Name}</h5>
                            {context.request.user.isAuthenticated && renderPrice(
                                context as ICoreContext<IAppSettingsInContext>,
                                typeName,
                                id,
                                showPrice,
                                !!breakoff,
                                product,
                                simpleProduct,
                                data.unitConversions,
                                savingsText,
                                freePriceText,
                                originalPriceText,
                                currentPriceText,
                                depositSuffixText,
                                pricePerText,
                                isPriceMinMaxEnabled,
                                priceResources,
                                priceErrorText,
                                randomWidth,
                                !compactView
                            )}
                            {renderDescription(product.Description)}
                        </div>
                    </div>
                </a>
            ) : (
                <a
                    href={productPageUrl}
                    onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                    aria-label={renderLabel(
                        product.Name,
                        formatCurrencyNOK(product.Price),
                        product.AverageRating,
                        ratingAriaLabel
                    )}
                    className={classnames('msc-product', { 'logged-in': context.request.user.isAuthenticated })}
                    {...attribute}
                >
                    <div className='msc-product__image-container'>
                        <div className='msc-product__image'>
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context,
                                productIndex !== undefined && productIndex < highPriorityProductComponentCount ? ImageFetchPriority.High : undefined
                            )}
                        </div>
                        {compactView ? additionalInformation : undefined}
                        {product.SpecialBadges && (
                            <div className='msc-product__special-badges'>
                                {product.SpecialBadges.split('|').map((badge: string, index: number) => {
                                    const brandScheme = data.brandSchemes?.find(x => x.BrandSchemeId === badge);

                                    if (!brandScheme) {
                                        return null;
                                    }

                                    return (
                                        <img
                                            key={`${product.RecordId}-${brandScheme?.BrandSchemeId}`}
                                            src={brandScheme?.ImageUrl}
                                            alt={brandScheme?.BrandSchemeName}
                                            title={brandScheme?.BrandSchemeName} />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className='msc-product__details'>
                        {!compactView && (
                            <div className='msc-product__category' style={{ whiteSpace: product.Category ? undefined : 'pre' }}>
                                {product.Category || ' '}
                            </div>
                        )}
                        <h5 className='msc-product__title' title={product.Name}>
                            <div className='msc-product__title__name'>
                                {product.Transit && (
                                    <div className={`${baseClassName}__transit-mark`} title={transitTooltip || 'Transittvare'}>T</div>
                                )}
                                {hasAgreementWithoutIcon && (
                                    <div className={`${baseClassName}__agreement-product-svg`} title={agreementProductTooltip || 'Avtalevare'}>
                                        <AgreementMark height={22} width={22} />
                                    </div>
                                )}
                                {agreementIcons?.map((icon, index) => (
                                    <img
                                        key={index}
                                        className={`${baseClassName}__agreement-product-icon`}
                                        src={getAgreementIconSource(icon.IconFileName!, assetPath => getAsset(assetPath, context.request))}
                                        title={icon.IconTooltipText || icon.TooltipText}
                                    />
                                ))}
                                {temperatureZoneIcon && (
                                    <div
                                        className={`${baseClassName}__temperature-icon ${temperatureZoneIcon}`}
                                        title={product.TemperatureZone} />
                                )}
                                <span>
                                    {product.Name}
                                </span>
                            </div>
                        </h5>
                        <div className='msc-product__sub-title-section'>
                            <div className='msc-product__sku'>{product.ItemId}</div>
                            {!compactView && additionalInformation}
                            <div
                                className={classnames('msc-product__unit-conversion', { enabled: unitConversionDetailsEnabled })}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowUnitConversionDetails(!showUnitConversionDetails);
                                }}
                            >
                                {getConversionText(
                                    context,
                                    simpleProduct?.DefaultUnitOfMeasure || product.DefaultUnitOfMeasure,
                                    data.unitConversions || [],
                                    simpleProduct,
                                    !!breakoff,
                                    product
                                )}
                                <UnitConversionDetails
                                    context={context}
                                    enabled={unitConversionDetailsEnabled}
                                    isOpen={showUnitConversionDetails}
                                    toggle={() => setShowUnitConversionDetails(!showUnitConversionDetails)}
                                    productName={product.Name}
                                    productUOM={simpleProduct?.DefaultUnitOfMeasure || product.DefaultUnitOfMeasure}
                                    unitConversions={data.unitConversions || []}
                                    simpleProduct={simpleProduct}
                                    breakoff={breakoff}
                                />
                            </div>
                        </div>
                    </div>
                    {context.request.user.isAuthenticated && !allowBuying && (
                        <div className='msc-product__prices' />
                    )}
                    {context.request.user.isAuthenticated && allowBuying && renderPrice(
                        context as ICoreContext<IAppSettingsInContext>,
                        typeName,
                        id,
                        showPrice,
                        !!breakoff,
                        product,
                        simpleProduct,
                        data.unitConversions,
                        savingsText,
                        freePriceText,
                        originalPriceText,
                        currentPriceText,
                        depositSuffixText,
                        pricePerText,
                        undefined,
                        undefined,
                        priceErrorText,
                        randomWidth,
                        !compactView
                    )}
                    {!context.request.user.isAuthenticated && (
                        <div className='msc-product__prices-not-logged-in'>
                            {priceNotLoggedInText || 'Logg inn for å se priser'}
                        </div>
                    )}
                </a>
            )}
            {renderProductDimensions(product.AttributeValues)}
            {!compactView && renderPurchaseControls()}
            <div className='msc-product__buy-section'>
                {compactView && context.request.user.isAuthenticated && allowBuying && (
                    <LocalDropdown
                        floatClassName={`${baseClassName}__compact-details-panel ${baseClassName}__compact-details-panel--overlap`}
                        backdropClassName={`${baseClassName}__compact-details-panel-backdrop`}
                        floatingId={compactDetailsPanelId}
                        initialFocusSelector='.quantity-input-v2__quantity-controls__input'
                        initialHoverSelector='.quantity-input-v2__quantity-controls__plus'
                        labelledBy={compactDetailsPanelHeadingId}
                        alignControlToReference
                        openAnimationMs={200}
                        closeAnimationMs={200}
                        closeAnimationSelector='.msc-product__compact-details-panel-content'
                        bottomSheetBreakpoint={540}
                        bottomSheetDismissible
                        matchWidth={false}
                        // Keep the quantity control over the trigger's original
                        // position, so the plus button is immediately under the
                        // pointer after opening.
                        offset={-84}
                        placement='bottom-end'
                        preserveAlignmentOnFlip
                        popupRole='dialog'
                        returnFocusOnClose
                        trigger={({ ref, onClick, 'aria-expanded': ariaExpanded }) => (
                            <button
                                ref={ref as any}
                                className={classnames('msc-add-to-cart', 'msc-product__compact-add-trigger', { 'is-open': ariaExpanded })}
                                type='button'
                                onClick={onClick}
                                aria-expanded={ariaExpanded}
                                aria-controls={compactDetailsPanelId}
                                aria-haspopup='dialog'
                                aria-label={addToCartText || 'Legg i kurv'}
                                title={addToCartText || 'Legg i kurv'}
                            >
                                {addToCartText || 'Legg i kurv'}
                            </button>
                        )}
                        >
                        {() => (
                            <div className='msc-product__compact-details-panel-content'>
                                <div className='msc-product__compact-details-panel-grab-handle' aria-hidden={true} />
                                <h2 className='msc-product__compact-details-panel-header' id={compactDetailsPanelHeadingId}>
                                    {selectQuantityText}
                                </h2>
                                <div className='msc-product__compact-details-panel-product-preview'>
                                    <div className='msc-product__compact-details-panel-product-preview-image'>
                                        {renderProductPlacementImage(
                                            imageSettings,
                                            context.request.gridSettings,
                                            productImageUrl,
                                            product.PrimaryImageUrl,
                                            product.Name,
                                            context
                                        )}
                                    </div>
                                    <span className='msc-product__compact-details-panel-product-preview-details'>
                                        <span className='msc-product__compact-details-panel-product-preview-name'>{product.Name}</span>
                                        <span className='msc-product__sku msc-product__compact-details-panel-product-preview-item-id'>{product.ItemId}</span>
                                    </span>
                                </div>
                                {renderPurchaseControls(renderCompactDeposit())}
                                {_renderCartButton()}
                                {renderProductAvailability(context, inventoryLabel, inventoryStatus, simpleProduct)}
                            </div>
                        )}
                    </LocalDropdown>
                )}
                {showAlternativeProductsType && (
                    <>
                        <LocalDropdown
                            floatClassName={`${baseClassName}__alternative-products-dropdown`}
                            openBodyClassName='alt-product-dropdown-open'
                            placement='bottom-start'
                            trigger={({ ref, onClick, 'aria-expanded': ariaExpanded }) => (
                                <button
                                    ref={ref as any}
                                    className={classnames('msc-product__change-product-button', { primary: showAlternativeProductsType === 'Primary' })}
                                    onClick={() => {
                                        void GTM.beginAltProductReplaceedAsync(product);
                                        onClick();
                                    }}
                                    aria-expanded={ariaExpanded}
                                >
                                    {props.alternativeProductsButtonText || 'Se alternativer'}
                                </button>
                            )}
                        >
                            {(close) => (
                                <AlternativeProducts
                                    id={id}
                                    typeName={typeName}
                                    context={context}
                                    mode={props.alternativeProductsMode || 'view'}
                                    resources={{
                                        alternativeProductsChangeProductButtonText: props.alternativeProductsChangeProductButtonText || 'Bytt produkt',
                                        alternativeProductsLoadingText: props.alternativeProductsLoadingText || 'Laster alternativer...',
                                        alternativeProductsNoResultsText: props.alternativeProductsNoResultsText || 'Ingen alternativer funnet',
                                        alternativeProductsErrorText: props.alternativeProductsErrorText || 'Kunne ikke laste alternativer',
                                        alternativeProductsCloseButtonText: props.alternativeProductsCloseButtonText || 'Lukk'
                                    }}
                                    product={{
                                        ...product,
                                        BreakoffProductKey: product.RecordId.toString(),
                                        Availability: inventoryStatus
                                    }}
                                    relationTypesByIds={data.relationTypesByIds}
                                    onChangeProduct={props.onChangeProduct}
                                    close={close}
                                />
                            )}
                        </LocalDropdown>
                    </>
                )}
                {compactView && context.request.user.isAuthenticated && (
                    <div className='msc-product__compact-availability-container'>
                        {renderProductAvailability(context, inventoryLabel, inventoryStatus, simpleProduct, true)}
                    </div>
                )}
                {!compactView && _renderCartButton()}
            </div>
            <div className='msc-product__bottom'>
                {context.request.user.isAuthenticated && allowBuying && renderProductAvailability(context, inventoryLabel, inventoryStatus, simpleProduct)}
                <div className='msc-product__bottom__indicators'>
                    {context.request.user.isAuthenticated && (
                        <CartCount
                            context={context}
                            cart={data.cart?.cart}
                            product={simpleProduct}
                            nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, data.unitConversions)}
                            inCartTooltip={inCartTooltipText}
                        />
                    )}
                    {context.request.user.isAuthenticated && _renderOrderTemplateIndicator()}
                </div>
            </div>
            {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)}
            <div
                className={classnames('card-actions-wrapper', { open: cardActionsOpen })}
                tabIndex={0}
                onClick={(e) => {
                    if (e.target === cardActionWrapperRef.current) {
                        setCardActionsOpen(!cardActionsOpen);
                    }
                }}
                onBlur={(e) => {
                    if (!cardActionWrapperRef.current?.contains(e.relatedTarget as Node)) {
                        setCardActionsOpen(false);
                    }
                }}
                ref={cardActionWrapperRef}
            >
                <div className='card-actions'>
                    {context.request.user.isAuthenticated && (
                        <>
                            {renderOrderTemplateButton()}
                            {wishlistButton && renderWishlistButton(wishlistButton)}
                        </>
                    )}
                </div>
            </div>

            {(product.Badge || agreementBadge) && (
                <div className='msc-product__image-badge-wrapper'>
                    {product.Badge && (
                        <DiscountBadge
                            context={context}
                            badge={product.Badge}
                            size={compactView ? 'medium' : 'large'}
                            sizeMultiplier={2} />
                    )}
                    {agreementBadge?.Badge && (
                        <DiscountBadge
                            context={context}
                            badge={agreementBadge.Badge}
                            size={compactView ? 'medium' : 'large'}
                            title={agreementBadge.TooltipText}
                            sizeMultiplier={2} />
                    )}
                </div>
            )}
        </div>
    );
};

function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function renderDescription(description?: string): JSX.Element | null {
    return <p className='msc-product__text'>{description}</p>;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

function renderRating(
    coreContext: ICoreContext,
    moduleTypeName: string,
    moduleId: string,
    avgRating?: number,
    totalRatings?: number,
    ariaLabel?: string,
    ratingCountAriaLabel?: string
): JSX.Element | null {
    if (!avgRating) {
        return null;
    }

    const numberRatings = totalRatings?.toString() || undefined;
    const ratingAriaLabelText = getRatingAriaLabel(avgRating, ariaLabel);
    const ratingCountAriaLabelText = getReviewAriaLabel(Number(numberRatings), ratingCountAriaLabel);

    return (
        <RatingComponent
            context={coreContext}
            id={moduleId}
            typeName={moduleTypeName}
            avgRating={avgRating}
            ratingCount={numberRatings}
            readOnly
            ariaLabel={ratingAriaLabelText}
            ratingCountAriaLabel={ratingCountAriaLabelText}
            data={{}}
        />
    );
}

function renderPrice(
    context: ICoreContext<IAppSettingsInContext>,
    typeName: string,
    id: string,
    show: boolean,
    breakoff: boolean,
    product?: ProductSearchResultExt,
    simpleProduct?: SimpleProductExt,
    unitConversions?: UnitOfMeasureConversion[] | undefined,
    savingsText?: string,
    freePriceText?: string,
    originalPriceText?: string,
    currentPriceText?: string,
    depositSuffixText?: string,
    pricePerText?: string,
    isPriceMinMaxEnabled?: boolean,
    priceResources?: IPriceComponentResources,
    priceErrorText?: string,
    randomWidth?: number,
    showDeposit = true
): JSX.Element | null {
    const price: ProductPrice = {
        BasePrice: product?.BasePrice,
        AdjustedPrice: product?.Price,
        CustomerContextualPrice: product?.Price
    };

    if (product?.PriceFailed) {
        return (
            <div className='msc-product__prices'>
                <span className='msc-price'>
                    <span className='msc-price__actual error'>
                        {priceErrorText || 'Pris n/a'}
                    </span>
                </span>
            </div>
        )
    }

    if (!show || !simpleProduct) {
        return (
            <div className='msc-product__prices'>
                <span className='msc-price'>
                    <span className='msc-price__actual retrieving' style={{ width: `${randomWidth}px` }}>
                        ...
                    </span>
                </span>
                <div className='msc-product__prices__extra'>
                    <div className='per-price__wrapper retrieving'>
                        ...
                    </div>
                </div>
            </div>
        )
    }

    const deposit = context.app.config?.enableDepositPriceDisplay && simpleProduct?.ExtensionProperties?.find(x => x.Key === 'DEPOSIT')?.Value?.DecimalValue;
    let productPrice = { ...(product?.ProductPrice || price) };

    if (!breakoff && simpleProduct?.Behavior?.MultipleOfQuantity) {
        productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior.MultipleOfQuantity);
    }

    const unitOfMeasure = simpleProduct?.DefaultUnitOfMeasure || product?.DefaultUnitOfMeasure;
    // Check if we should kg price for this product based on category settings
    const hideConversionInCategories = getHideConversionInCategories(context);
    const dontShowKgPrice = isProductInCategory(product, hideConversionInCategories);
    const conversions = unitConversions?.filter(x => x.FromUnitOfMeasureSymbol === unitOfMeasure);
    const kilogramConversion = !dontShowKgPrice && conversions && getKilogramConversion(unitOfMeasure, conversions);
    const pcsConversion = conversions && getPcsConversion(conversions, unitOfMeasure);

    return (
        <div className='msc-product__prices'>
            <div>
                <PriceComponent
                    context={context}
                    id={id}
                    className='price'
                    typeName={typeName}
                    data={{ price: productPrice }}
                    savingsText={savingsText}
                    freePriceText={freePriceText}
                    originalPriceText={originalPriceText}
                    isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                    priceResources={priceResources}
                />
            </div>
            <div className='msc-product__prices__extra'>
                {!!(simpleProduct?.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1 && !breakoff) && (
                    <div>
                        <PriceComponent
                            context={context}
                            id={id}
                            typeName={typeName}
                            data={{
                                price: {
                                    CustomerContextualPrice: product?.ProductPrice?.CustomerContextualPrice || price.CustomerContextualPrice || 0
                                }
                            }}
                            savingsText={savingsText}
                            freePriceText={freePriceText}
                            originalPriceText={originalPriceText}
                            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                            priceResources={priceResources}
                        />
                        <span className='price-suffix'>
                            &nbsp;
                            {format(pricePerText || 'pr. {0}', humanizeUnitOfMeasure(context, simpleProduct.DefaultUnitOfMeasure, simpleProduct).toLocaleLowerCase())}
                        </span>
                    </div>
                )}
                {!!(price.CustomerContextualPrice && pcsConversion) && (
                    <div>
                        <PriceComponent
                            context={context}
                            id={id}
                            typeName={typeName}
                            data={{
                                price: {
                                    CustomerContextualPrice: price.CustomerContextualPrice / (pcsConversion.Factor || 1)
                                }
                            }}
                            savingsText={savingsText}
                            freePriceText={freePriceText}
                            originalPriceText={originalPriceText}
                            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                            priceResources={priceResources}
                        />
                        <span className='price-suffix'>
                            &nbsp;pr. stk
                        </span>
                    </div>
                )}
                {!!(price.CustomerContextualPrice && kilogramConversion && getRoundedFactor(kilogramConversion) > 0) && (
                    <div>
                        <PriceComponent
                            context={context}
                            id={id}
                            typeName={typeName}
                            data={{
                                price: {
                                    CustomerContextualPrice: price.CustomerContextualPrice / getRoundedFactor(kilogramConversion)
                                }
                            }}
                            savingsText={savingsText}
                            freePriceText={freePriceText}
                            originalPriceText={originalPriceText}
                            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                            priceResources={priceResources}
                        />
                        <span className='price-suffix'>
                            &nbsp;pr. kg
                        </span>
                    </div>
                )}
                {!!(price.CustomerContextualPrice && conversions && getLiterPrice(conversions, unitOfMeasure, price.CustomerContextualPrice)) && (
                    <div>
                        <PriceComponent
                            context={context}
                            id={id}
                            typeName={typeName}
                            data={{
                                price: {
                                    CustomerContextualPrice: getLiterPrice(conversions, unitOfMeasure, price.CustomerContextualPrice)
                                }
                            }}
                            savingsText={savingsText}
                            freePriceText={freePriceText}
                            originalPriceText={originalPriceText}
                            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                            priceResources={priceResources}
                        />
                        <span className='price-suffix'>
                            &nbsp;pr. liter
                        </span>
                    </div>
                )}
            </div>
            {showDeposit && deposit && deposit > 0 ? (
                <div>
                    <div className='deposit__wrapper'>
                        <span>+{' '}</span>
                        <PriceComponent
                            context={context}
                            id={id}
                            className='deposit'
                            typeName={typeName}
                            data={{
                                price: {
                                    CustomerContextualPrice: deposit
                                }
                            }}
                            savingsText={savingsText}
                            freePriceText={freePriceText}
                            originalPriceText={originalPriceText}
                            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                            priceResources={priceResources}
                            />
                        <span>{` ${depositSuffixText}`}</span>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>,
    fetchPriority?: ImageFetchPriority
): JSX.Element | null {
    if (!gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = context?.request.apiSettings.baseImageUrl;
    if (fallbackImageSource?.endsWith('/')) {
        fallbackImageSource = fallbackImageSource.slice(0, -1);
    }
    if (emptyPlaceHolderImage) {
        fallbackImageSource = `${fallbackImageSource}${emptyPlaceHolderImage}`;
    }
    imageUrl = ensureBaseImageUrl(context as ICoreContext, imageUrl);
    fallbackImageUrl = ensureBaseImageUrl(context as ICoreContext, fallbackImageUrl);
    const img: IImageData = {
        src: imageUrl || fallbackImageSource,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    imageProps.imageSettings.mode = 0;
    const sharedViewports = {
        h: 200, w: 200, q: 'w=200&h=200&q=80&m=0&f=webp', qe: 't=200h200qhigh'
    };
    imageProps.imageSettings.viewports = {
        xs: { ...sharedViewports },
        lg: { ...sharedViewports },
        md: { ...sharedViewports },
        sm: { ...sharedViewports },
        xl: { ...sharedViewports }
    };

    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            fetchPriority={fetchPriority}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<IProductComponent>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
